import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PromoEntity } from './promo.entity';

// Polymorphic targeting for promotions, in two dimensions.
//
// COMPANY rows say WHERE the promo runs. Every promo carries at least one
// (PD-14779) — a promo with no company has nowhere to run, and the pre-
// PD-14779 "no COMPANY rows means everywhere" reading made such a promo
// editable only by a user with access to every company, so its own author
// could not delete it.
//
// CLIENT / CLIENT_GROUP / CLIENT_BUSINESS_TYPE rows are the audience
// dimension: they optionally narrow the promo to certain customers WITHIN
// those companies, and are OR-combined with each other. No audience rows
// means every customer of the promo's companies.
//
// The two dimensions are AND-ed. See `targetsClient` in
// libs/core/src/utils/promo-pricing/eligibility.ts for the evaluation.
@Index('idx_promo_target_promo', ['fkPromoId'])
@Index('idx_promo_target_resolve', ['targetType', 'targetRefId'])
@Index('idx_promo_target_value', ['targetType', 'targetRefValue'])
@Entity('promo_target')
export class PromoTargetEntity extends BaseEntity {
	@Column('int', { name: 'fk_promo__id', unsigned: true })
	public fkPromoId!: number;

	// COMPANY / CLIENT / CLIENT_GROUP use targetRefId.
	// CLIENT_BUSINESS_TYPE uses targetRefValue. TENANT is a legacy,
	// dimensionless marker that constrains nothing; the editor no longer
	// writes one, and the engine ignores it.
	@Column('varchar', { name: 'targetType', length: 32 })
	public targetType!:
		| 'TENANT'
		| 'COMPANY'
		| 'CLIENT'
		| 'CLIENT_GROUP'
		| 'CLIENT_BUSINESS_TYPE';

	@Column('int', { name: 'targetRefId', nullable: true, unsigned: true })
	public targetRefId!: number | null;

	@Column('varchar', { name: 'targetRefValue', nullable: true, length: 255 })
	public targetRefValue!: string | null;

	@ManyToOne(() => PromoEntity, (promo) => promo.promoTargets, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_promo__id', referencedColumnName: 'id' }])
	public fkPromo!: PromoEntity;
}
